/**
 * Client for the Launchpad web-onboarding admin API.
 *
 * In deployed environments requests go through the same-origin Vercel proxy
 * (`/api/launchpad`, see src/server/launchpadProxy.ts) which injects the shared
 * bot key server-side. For local `vite dev` (no /api functions) set
 * `VITE_LAUNCHPAD_API_URL` (+ `VITE_LAUNCHPAD_API_KEY`) to talk to a bot
 * directly.
 *
 * Launchpad is the Meta-ads web portal: a visitor picks an occasion + uploads
 * media on the web, then messages WhatsApp with an `EFL-` code. The bot's
 * funnel here is token-keyed and pre-WhatsApp (session starts / finalized
 * uploads live here, not in the wa_id-keyed dashboard funnel).
 */

/** One stage in the bot's token-keyed pre-WhatsApp launchpad funnel. */
export type LaunchpadFunnelStage = {
  key: string;
  label: string;
  count: number;
};

/** Per-utm_campaign slice of the launchpad funnel ("(none)" for untagged). */
export type LaunchpadCampaignFunnel = {
  campaign: string;
  cohort_size: number;
  stages: LaunchpadFunnelStage[];
  started_not_messaged: number;
};

/**
 * Aggregate launchpad funnel from the bot (`GET /launchpad/v1/admin/funnel`).
 * `tracking_since` is null when no sessions have been recorded.
 */
export type LaunchpadFunnel = {
  params: { range: string; campaign?: string | null; window_hours: number };
  tracking_since: string | null;
  cohort_size: number;
  stages: LaunchpadFunnelStage[];
  started_not_messaged: number;
  started_not_messaged_settled: number;
  incomplete_cohort: boolean;
  by_campaign: LaunchpadCampaignFunnel[];
};

function apiTarget(): { base: string; headers: Record<string, string> } {
  const direct = import.meta.env.VITE_LAUNCHPAD_API_URL as string | undefined;
  if (direct) {
    const key = import.meta.env.VITE_LAUNCHPAD_API_KEY as string | undefined;
    return {
      base: `${direct.replace(/\/$/, "")}/launchpad/v1`,
      headers: key ? { "X-Launchpad-Key": key } : {},
    };
  }
  const proxy =
    (import.meta.env.VITE_LAUNCHPAD_PROXY_PATH as string | undefined) ||
    "/api/launchpad";
  return { base: proxy.replace(/\/$/, ""), headers: {} };
}

async function launchpadRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const { base, headers } = apiTarget();
  const response = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...headers,
      ...(init.headers || {}),
    },
  });
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(payload?.error || `Launchpad API error ${response.status}`);
  }
  if (payload === null) {
    // An HTML body with a 200 means the SPA catch-all swallowed the proxy
    // route (vercel.json rewrite missing) — surface it instead of rendering
    // an empty funnel.
    throw new Error(
      "Launchpad API returned a non-JSON response (proxy misrouted)",
    );
  }
  return payload as T;
}

/**
 * Fetch the bot's pre-WhatsApp launchpad funnel for a given CRM range. The bot
 * aligns its ranges to the CRM's (`this_week`/`30d`/`90d`/`all`); pass the same
 * range the dashboard funnel uses so the two windows line up. `campaign`
 * narrows to a single utm_campaign when provided.
 */
export async function fetchLaunchpadFunnel(
  range: string,
  campaign?: string,
): Promise<LaunchpadFunnel> {
  const params = new URLSearchParams({ range });
  if (campaign) params.set("campaign", campaign);
  return launchpadRequest<LaunchpadFunnel>(
    `/admin/funnel?${params.toString()}`,
  );
}
